import { useEffect, useState, useRef } from 'react';
import { getCompanyInfo, updateCompanyInfo, uploadMedia, uploadFavicon } from '../adminApi';
import { toast } from 'sonner';
import { Save, Upload, X, Globe } from 'lucide-react';

const EMPTY_FORM = {
  phone: '',
  email: '',
  address: '',
  working_hours: '',
  maps_url: '',
  instagram: '',
  facebook: '',
  youtube: '',
  linkedin: '',
  logo_url: '',
  favicon_url: '',
};

// ── Favicon validation ─────────────────────────────────────────────────────────

const FAVICON_ALLOWED_EXTS = ['ico', 'png', 'svg'];
const FAVICON_MAX_BYTES = 512 * 1024; // 512 KB
const FAVICON_VALID_SIZES = [16, 32, 48, 64, 128, 180, 192, 256, 512];

async function validateFavicon(file) {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!FAVICON_ALLOWED_EXTS.includes(ext)) {
    return `Geçersiz dosya türü: .${ext}. Yalnızca .ico, .png veya .svg kabul edilir.`;
  }
  if (file.size > FAVICON_MAX_BYTES) {
    return `Dosya çok büyük: ${Math.round(file.size / 1024)} KB. Maksimum 512 KB.`;
  }
  if (ext === 'png') {
    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== height) {
        return `PNG favicon kare olmalıdır (${width}×${height} px algılandı). Önerilen boyutlar: 16×16, 32×32, 48×48, 180×180, 192×192 veya 512×512.`;
      }
      if (!FAVICON_VALID_SIZES.includes(width)) {
        // Warn but don't block — just inform
        toast.warning(`Favicon boyutu ${width}×${height} px. Önerilen: 32×32, 48×48, 192×192 veya 512×512 px.`);
      }
    } catch {
      // dimension check failed — proceed anyway
    }
  }
  return null; // valid
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Görsel okunamadı'));
    };
    img.src = url;
  });
}

// ── Image upload field ─────────────────────────────────────────────────────────

function ImageUploadField({ label, name, value, onChange, hint, previewClass = 'h-10' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMedia(file, 'branding');
      onChange(result.url);
      toast.success(`${label} yüklendi.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
        {label}
      </label>
      <div className="flex gap-2 items-start flex-wrap">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL veya aşağıdan yükle"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 transition-all"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 bg-white/[0.05] border border-white/[0.08] hover:border-[#c5a059]/40 hover:bg-[#c5a059]/[0.06] text-white/55 hover:text-white/80 rounded-lg px-3 py-2.5 text-xs transition-all disabled:opacity-40 flex-shrink-0"
        >
          <Upload size={13} />
          {uploading ? 'Yükleniyor…' : 'Yükle'}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="p-2.5 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
            title="Kaldır"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {value && (
        <div className="mt-2 flex items-center gap-2">
          <img
            src={value}
            alt={label}
            className={`${previewClass} w-auto object-contain rounded border border-white/[0.07] bg-white/[0.02] p-1`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      {hint && <p className="text-[10px] text-white/25 mt-1.5">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

// ── Favicon upload field ───────────────────────────────────────────────────────

function FaviconUploadField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const error = await validateFavicon(file);
    if (error) {
      toast.error(error);
      return;
    }
    setUploading(true);
    try {
      const result = await uploadFavicon(file);
      onChange(result.url);
      toast.success('Favicon yüklendi.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
        Favicon
      </label>
      <div className="flex gap-2 items-start flex-wrap">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL veya aşağıdan yükle"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 transition-all"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 bg-white/[0.05] border border-white/[0.08] hover:border-[#c5a059]/40 hover:bg-[#c5a059]/[0.06] text-white/55 hover:text-white/80 rounded-lg px-3 py-2.5 text-xs transition-all disabled:opacity-40 flex-shrink-0"
        >
          <Upload size={13} />
          {uploading ? 'Yükleniyor…' : 'Yükle'}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="p-2.5 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
            title="Kaldır"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {value && (
        <div className="mt-2 flex items-center gap-2">
          <img
            src={value}
            alt="Favicon"
            className="h-8 w-8 object-contain rounded border border-white/[0.07] bg-white/[0.02] p-0.5"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-[10px] text-white/30">32×32 px önizleme boyutu</span>
        </div>
      )}
      <p className="text-[10px] text-white/25 mt-1.5">
        Yalnızca .ico, .png veya .svg • Maksimum 512 KB • PNG için kare (örn. 32×32, 192×192, 512×512) önerilir.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function CompanyInfoPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCompanyInfo()
      .then((data) => {
        setForm({
          phone:          data.phone          ?? '',
          email:          data.email          ?? '',
          address:        data.address        ?? '',
          working_hours:  data.working_hours  ?? '',
          maps_url:       data.maps_url       ?? '',
          instagram:      data.instagram      ?? '',
          facebook:       data.facebook       ?? '',
          youtube:        data.youtube        ?? '',
          linkedin:       data.linkedin       ?? '',
          logo_url:       data.logo_url       ?? '',
          favicon_url:    data.favicon_url    ?? '',
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompanyInfo(form);
      toast.success('Şirket bilgileri güncellendi.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;
  if (error) return <PageError message={error} />;

  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <div className="mb-7">
        <h1 className="text-xl font-bold text-white tracking-tight">Şirket Bilgileri</h1>
        <p className="text-white/35 text-sm mt-0.5">Siteye yansıyan iletişim bilgilerini ve görselleri güncelle</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── İletişim ─────────────────────────────────────────────── */}
        <SectionCard title="İletişim Bilgileri">
          <FormField
            label="Telefon"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+90 530 000 0000"
          />
          <FormField
            label="E-posta"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="info@oberyapi.com"
          />
          <FormField
            label="Adres"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Tam adres"
            multiline
          />
          <FormField
            label="Çalışma Saatleri"
            name="working_hours"
            value={form.working_hours}
            onChange={handleChange}
            placeholder="Pzt - Cmt: 09:00 - 18:00"
          />
        </SectionCard>

        {/* ── Linkler ──────────────────────────────────────────────── */}
        <SectionCard title="Linkler">
          <div>
            <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
              Google Maps URL
            </label>
            <div className="flex gap-2 items-center">
              <Globe size={14} className="text-white/25 flex-shrink-0" />
              <input
                type="url"
                name="maps_url"
                value={form.maps_url}
                onChange={handleChange}
                placeholder="https://maps.google.com/..."
                className={INPUT}
              />
            </div>
          </div>
          {[
            { name: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/oberyapi' },
            { name: 'facebook',  label: 'Facebook URL',  placeholder: 'https://facebook.com/oberyapi'  },
            { name: 'youtube',   label: 'YouTube URL',   placeholder: 'https://youtube.com/@oberyapi'  },
            { name: 'linkedin',  label: 'LinkedIn URL',  placeholder: 'https://linkedin.com/company/oberyapi' },
          ].map(({ name, label, placeholder }) => (
            <div key={name}>
              <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
                {label}
              </label>
              <div className="flex gap-2 items-center">
                <Globe size={14} className="text-white/25 flex-shrink-0" />
                <input
                  type="url"
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  className={INPUT}
                />
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ── Logo ─────────────────────────────────────────────────── */}
        <SectionCard title="Logo">
          <ImageUploadField
            label="Site Logosu"
            name="logo_url"
            value={form.logo_url}
            onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))}
            previewClass="h-10"
            hint="PNG veya WEBP önerilir. Yatay format için en-boy oranı korunur. JPG, PNG, WEBP kabul edilir."
          />
        </SectionCard>

        {/* ── Favicon ──────────────────────────────────────────────── */}
        <SectionCard title="Favicon">
          <FaviconUploadField
            value={form.favicon_url}
            onChange={(url) => setForm((f) => ({ ...f, favicon_url: url }))}
          />
        </SectionCard>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#d4af6a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            <Save size={14} />
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Shared components ──────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 focus:bg-white/[0.06] transition-all resize-none';

function SectionCard({ title, children }) {
  return (
    <div className="bg-[#111] border border-white/[0.07] rounded-xl p-6 space-y-5">
      <h2 className="text-[10px] text-white/30 uppercase tracking-[0.18em] pb-3 border-b border-white/[0.05] -mt-1">
        {title}
      </h2>
      {children}
    </div>
  );
}

function FormField({ label, name, value, onChange, placeholder, type = 'text', multiline }) {
  return (
    <div>
      <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
        {label}
      </label>
      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={3}
          className={INPUT}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={INPUT}
        />
      )}
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}
